import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import Header from '../../components/Header';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../api';

const GRUPOS = [
  { titulo: 'Diretoria', tipos: ['ceo', 'diretoria'] },
  { titulo: 'Gestão (tem Minha Equipe)', tipos: ['gerencia', 'supervisao'] },
  { titulo: 'Operacional', tipos: ['operacional'] },
  { titulo: 'Auxiliar', tipos: ['auxiliar'] },
];
const EQUIPE = {
  total: { l: 'Acompanha todos', c: 'alerta' },
  propria: { l: 'Minha Equipe', c: 'ok' },
};
const toast = (title) => Swal.fire({ toast: true, position: 'bottom-end', icon: 'success', title, timer: 3000, showConfirmButton: false });

export default function Cargos() {
  const { temPermissao } = useAuth();
  // Diretoria/CEO/supervisão acompanham todo mundo: só admin distribui (o backend também barra).
  const podeAdmin = temPermissao('gerir_perfis');
  const protegida = (op) => !podeAdmin && ['ceo', 'diretoria', 'supervisao'].includes(op.tipo);
  const [cargos, setCargos] = useState([]);
  const [perfis, setPerfis] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState('');
  const [soSem, setSoSem] = useState(false);
  const [salvando, setSalvando] = useState(null);

  useEffect(() => {
    api.get('/cargos')
      .then(({ data }) => { setCargos(data.cargos); setPerfis(data.perfis); })
      .catch(err => Swal.fire('Erro', err.response?.data?.error || 'Não foi possível carregar os cargos.', 'error'))
      .finally(() => setCarregando(false));
  }, []);

  const perfilPorId = useMemo(() => Object.fromEntries(perfis.map(p => [p.id, p])), [perfis]);
  const grupos = useMemo(() => {
    const usados = new Set(GRUPOS.flatMap(g => g.tipos));
    return [
      ...GRUPOS.map(g => ({ titulo: g.titulo, perfis: perfis.filter(p => g.tipos.includes(p.tipo)) })),
      { titulo: 'Outros', perfis: perfis.filter(p => !usados.has(p.tipo)) },
    ].filter(g => g.perfis.length);
  }, [perfis]);

  const lista = cargos.filter(c =>
    (!busca.trim() || c.cargo.toLowerCase().includes(busca.trim().toLowerCase())) && (!soSem || !c.perfil_id)
  );
  const semPerfil = cargos.filter(c => !c.perfil_id);

  async function mudar(c, valor) {
    const pid = valor ? parseInt(valor) : null;
    const novo = pid ? perfilPorId[pid] : null;
    if (novo?.acesso_equipe === 'total') {
      const r = await Swal.fire({
        icon: 'warning', title: 'Esse perfil acompanha todo mundo',
        text: `Quem tem o cargo "${c.cargo}" vai ver o progresso e as leituras de TODOS os colaboradores. Confirmar?`,
        showCancelButton: true, confirmButtonText: 'Sim, confirmar', cancelButtonText: 'Cancelar',
      });
      if (!r.isConfirmed) return;
    }
    if (!pid && c.perfil_id) {
      const r = await Swal.fire({
        icon: 'warning', title: `Tirar o perfil de "${c.cargo}"?`,
        text: `As ${c.pessoas} pessoa(s) desse cargo ficam só com o acesso básico e saem da posição no organograma.`,
        showCancelButton: true, confirmButtonText: 'Sim, tirar', cancelButtonText: 'Cancelar',
      });
      if (!r.isConfirmed) return;
    }
    setSalvando(c.cargo);
    try {
      await api.put('/cargos', { cargo: c.cargo, perfil_id: pid });
      setCargos(prev => prev.map(x => (x.cargo === c.cargo
        ? { ...x, perfil_id: pid, perfil_titulo: novo ? novo.titulo : null, atualizado_em: new Date().toISOString() }
        : x)));
      toast('Salvo. O organograma já mudou; o acesso muda no próximo login.');
    } catch (err) {
      Swal.fire('Erro', err.response?.data?.error || 'Não foi possível salvar.', 'error');
    } finally {
      setSalvando(null);
    }
  }

  return (
    <>
      <Header />
      <main className="conteudo-container" style={{ maxWidth: 1040 }}>
        <Link to="/admin" className="doc-voltar"><i className="bi bi-arrow-left"></i> Administração</Link>
        <div className="adm-topo">
          <div>
            <h1>Cargos</h1>
            <p>
              Cada cargo do cadastro do RH vira um perfil aqui no portal. É isso que define onde a pessoa aparece no organograma,
              que trilhas e documentos são dela e se ela acompanha uma equipe.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Link to="/admin/organograma" className="btn btn-outline-secondary"><i className="bi bi-diagram-3 me-1"></i> Posições</Link>
            <Link to="/admin/acessos" className="btn btn-outline-secondary"><i className="bi bi-person-badge me-1"></i> Colaboradores</Link>
          </div>
        </div>

        {!carregando && semPerfil.length > 0 && (
          <div className="conteudo-card" style={{ borderLeft: '4px solid #E07A0B', marginBottom: 12 }}>
            <strong>{semPerfil.length} cargo(s) sem perfil</strong>{' '}
            <span className="adm-dica">— quem tem esses cargos só enxerga o básico e não aparece no organograma:
              {' '}{semPerfil.map(c => `${c.cargo} (${c.pessoas})`).join(', ')}.</span>
          </div>
        )}

        <div className="adm-filtros">
          <div className="doc-busca" style={{ flex: 1, minWidth: 220, marginBottom: 0 }}>
            <i className="bi bi-search"></i>
            <input type="text" placeholder="Buscar cargo…" value={busca} onChange={e => setBusca(e.target.value)} />
            {busca && <button type="button" className="doc-busca-limpar" onClick={() => setBusca('')}>×</button>}
          </div>
          <label className="doc-check" style={{ marginTop: 0 }}>
            <input type="checkbox" checked={soSem} onChange={e => setSoSem(e.target.checked)} /> Só os sem perfil
          </label>
        </div>

        {carregando ? <div>Carregando…</div> : (
          <div className="adm-tabela-wrap">
            <table className="adm-tabela">
              <thead>
                <tr><th>Cargo no RH</th><th className="adm-num">Pessoas</th><th>Perfil no portal</th><th>Gestão</th></tr>
              </thead>
              <tbody>
                {lista.map(c => {
                  const p = c.perfil_id ? perfilPorId[c.perfil_id] : null;
                  const eq = p?.acesso_equipe ? EQUIPE[p.acesso_equipe] : null;
                  return (
                    <tr key={c.cargo} className={!c.perfil_id ? 'adm-linha-alerta' : ''}>
                      <td>
                        <strong>{c.cargo}</strong>
                        {!c.perfil_id && <> <span className="adm-badge adm-badge--alerta">sem perfil</span></>}
                        {c.com_posicao_manual > 0 && (
                          <div className="adm-sub">{c.com_posicao_manual} com posição definida à mão (não muda com isto)</div>
                        )}
                      </td>
                      <td className="adm-num">{c.pessoas}</td>
                      <td>
                        <select className="doc-input" value={c.perfil_id ?? ''} disabled={salvando === c.cargo}
                                onChange={e => mudar(c, e.target.value)}>
                          <option value="">— sem perfil (só acesso básico) —</option>
                          {grupos.map(g => (
                            <optgroup key={g.titulo} label={g.titulo}>
                              {g.perfis.map(op => (
                                <option key={op.id} value={op.id} disabled={protegida(op)}>
                                  {op.titulo}{protegida(op) ? ' (só administrador)' : ''}
                                </option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                      </td>
                      <td>{eq ? <span className={`adm-badge adm-badge--${eq.c}`}>{eq.l}</span> : <span className="adm-sub">—</span>}</td>
                    </tr>
                  );
                })}
                {lista.length === 0 && <tr><td colSpan={4} className="adm-vazio">Nenhum cargo com esse filtro.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </>
  );
}
